/**
 * Connections list view state: the rule that keeps a followers / following /
 * buddies list honest while requests overlap.
 *
 * Rows are always stored with the key of the list they came from, and this
 * resolver hands back rows only while that key is the one being asked for. A
 * tab switch therefore cannot render the previous tab's people, and a response
 * that lands after the member moved on has nowhere to go. Kept free of React
 * and of any API import so the guarantee can be unit tested directly.
 */
import type { UserConnectionItem } from '../types/userProfile';

export type ConnectionsFetchMode = 'replace' | 'append';

/** A page of rows plus the identity of the list they belong to. */
export interface CommittedConnectionsPage {
  /** `profileUserId|relation`, or '' before any list has been asked for. */
  key: string;
  users: UserConnectionItem[];
  /** Highest page committed for this key, so the next fetch asks for page + 1. */
  page: number;
  hasMore: boolean;
  error: string;
}

export interface PendingConnectionsRequest {
  key: string;
  mode: ConnectionsFetchMode;
}

export interface ConnectionsView {
  users: UserConnectionItem[];
  page: number;
  hasMore: boolean;
  error: string;
  loading: boolean;
  loadingMore: boolean;
}

export const EMPTY_CONNECTIONS_PAGE: CommittedConnectionsPage = {
  key: '',
  users: [],
  page: 1,
  hasMore: false,
  error: '',
};

/**
 * @param requestKey identity of the list on screen, '' when there is nothing
 *   to load yet (the profile it belongs to has not resolved).
 */
export const resolveConnectionsView = (
  committed: CommittedConnectionsPage,
  pending: PendingConnectionsRequest | null,
  requestKey: string,
): ConnectionsView => {
  const current = committed.key === requestKey ? committed : EMPTY_CONNECTIONS_PAGE;
  const pendingMode = pending?.key === requestKey ? pending.mode : null;

  return {
    users: current.users,
    page: current.page,
    hasMore: current.hasMore,
    error: current.error,
    // Having no rows for this key yet counts as loading even before the request
    // starts, so the tab never shows an empty list for a fetch on its way.
    loading: requestKey !== '' && (current.key !== requestKey || pendingMode === 'replace'),
    loadingMore: pendingMode === 'append',
  };
};
